
import { Box, Button, Checkbox, Container, CssBaseline, FormControlLabel, Grid, Link, TextField, ThemeProvider, Typography, createTheme, useMediaQuery } from '@mui/material';
import { useEffect, useState } from 'react';
import Logo from '../../asset/logo/LOGO_ORANGE.svg';
import { useAppDispatch } from '../../redux';
import { AppDispatch } from '../../redux/store';
import { Copyright } from '../../utils/CopyRightPage';
import { useSignInForm } from '../../utils/forms';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { enableTwoFactor, setupTwoFactor, verifyTwoFactor } from '../../service/AuthenticationService';
import { showSuccessToast } from '../../utils/Toast';
import i18next from "i18next";

type Step = 'password' | 'code' | 'setup' | 'backupCodes';

const REMEMBERED_EMAIL = 'admin_remembered_email';

const theme = createTheme({
    palette: {
        primary: {
            main: '#EB6608',
        },
        secondary: {
            main: '#f50057',
        },
    },
});
export default function SignIn() {
    const [t] = useTranslation("global");
    
    const [, setFormSubmitted] = useState(false);
    const dispatch:AppDispatch =useAppDispatch();
    const isMobile = useMediaQuery('(max-width:600px)');
    const navigate = useNavigate();
    // Admin login: after the password comes a code from the authenticator app, or the 2FA set-up the first time
    const [step, setStep] = useState<Step>('password');
    const [challengeToken, setChallengeToken] = useState('');
    const [code, setCode] = useState('');
    const [setup, setSetup] = useState<{ secret: string; qrDataUrl: string } | null>(null);
    const [backupCodes, setBackupCodes] = useState<string[]>([]);
    const [busy, setBusy] = useState(false);

    const handleChallenge = async (data: any) => {
        setChallengeToken(data.challengeToken);
        setCode('');
        if (data.twoFactorSetupRequired) {
            const result = await setupTwoFactor(data.challengeToken, isMobile);
            if (result) {
                setSetup(result);
                setStep('setup');
            }
        } else {
            setStep('code');
        }
    };
    const formik = useSignInForm(dispatch, isMobile, handleChallenge);

    // "Husk mig" husker mailadressen på denne maskine. Selve sessionen er bevidst kort for admins (2FA og
    // timeout), så den forlænges ikke - men adressen skal ikke skrives igen hver gang.
    const [remember, setRemember] = useState(Boolean(localStorage.getItem(REMEMBERED_EMAIL)));
    useEffect(() => {
        const saved = localStorage.getItem(REMEMBERED_EMAIL);
        if (saved) formik.setFieldValue('email', saved);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (remember && formik.values.email) localStorage.setItem(REMEMBERED_EMAIL, formik.values.email);
        if (!remember) localStorage.removeItem(REMEMBERED_EMAIL);
    }, [remember, formik.values.email]);

    const signedIn = () => {
        showSuccessToast(t('messages.loginOk'), isMobile);
        navigate('/home');
    };

    const handleCode = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setBusy(true);
        if (step === 'code') {
            if (await verifyTwoFactor(challengeToken, code, dispatch, isMobile)) signedIn();
        } else {
            const result = await enableTwoFactor(challengeToken, code, dispatch, isMobile);
            if (result) {
                setBackupCodes(result.backupCodes);
                setStep('backupCodes');
            }
        }
        setBusy(false);
    };

    const backToPassword = () => {
        setStep('password');
        setChallengeToken('');
        setSetup(null);
        setCode('');
    };
    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        console.log("Entered");
        
        setFormSubmitted(true);
        formik.handleSubmit(e as any);
    };
    return (
        <ThemeProvider theme={theme} >
            <Container component="main" maxWidth="xs">
                <CssBaseline />
                <Box
                    sx={{
                        marginTop: 5,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <img src={Logo} alt="Logo" style={{ width: '40%', height: '20%' }} />
                    <div style={{ background: theme.palette.primary.main, width: '100%', display: 'flex', justifyContent: 'center', alignContent: 'center', borderRadius: '4px' }}>
                        <Typography component="h1" variant="h5" style={{
                            color: 'white', padding: '4px', marginTop: 3, marginBottom: 2
                        }}>
                            {t('signin.t8')}
                        </Typography>
                    </div>
                    {(step === 'code' || step === 'setup') && (
                        <Box component="form" onSubmit={handleCode} noValidate sx={{ mt: 2, width: '100%' }}>
                            <Typography variant="h6">{step === 'code' ? t('signin.twoFactorTitle') : t('signin.setupTitle')}</Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                {step === 'code' ? t('signin.twoFactorIntro') : t('signin.setupIntro')}
                            </Typography>
                            {step === 'setup' && setup && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2 }}>
                                    <img src={setup.qrDataUrl} alt="QR" style={{ width: 220, height: 220 }} />
                                    <Typography variant="body2" sx={{ mt: 1 }}>{t('signin.setupManual')}</Typography>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{setup.secret}</Typography>
                                </Box>
                            )}
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                id="code"
                                label={t('signin.code')}
                                name="code"
                                autoComplete="one-time-code"
                                autoFocus
                                inputProps={{ inputMode: 'numeric' }}
                                value={code}
                                onChange={(event) => setCode(event.target.value)}
                            />
                            <Button type="submit" fullWidth variant="contained" disabled={!code || busy} sx={{ mt: 3, mb: 2 }}>
                                {step === 'code' ? t('signin.confirm') : t('signin.activate')}
                            </Button>
                            <Link component="button" type="button" variant="body2" onClick={backToPassword}>
                                {t('signin.back')}
                            </Link>
                        </Box>
                    )}
                    {step === 'backupCodes' && (
                        <Box sx={{ mt: 2, width: '100%' }}>
                            <Typography variant="h6">{t('signin.backupTitle')}</Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>{t('signin.backupIntro')}</Typography>
                            <Grid container spacing={1} sx={{ mt: 1 }}>
                                {backupCodes.map((backupCode) => (
                                    <Grid item xs={6} key={backupCode}>
                                        <Typography sx={{ fontFamily: 'monospace', textAlign: 'center' }}>{backupCode}</Typography>
                                    </Grid>
                                ))}
                            </Grid>
                            <Button fullWidth variant="contained" onClick={signedIn} sx={{ mt: 3, mb: 2 }}>
                                {t('signin.backupDone')}
                            </Button>
                        </Box>
                    )}
                    {step === 'password' && (
                    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label={t('signin.t6')}
                            name="email"
                            autoComplete="email"
                            autoFocus
                            onChange={formik.handleChange("email")}
                            value={formik.values.email}
                            error={formik.touched.email && Boolean(formik.errors.email)}
                            helperText={formik.touched.email && formik.errors.email}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label={t('signin.t5')}
                            type="password"
                            id="password"
                            autoComplete="current-password"
                            onChange={formik.handleChange("password")}
                            value={formik.values.password}
                            error={formik.touched.password && Boolean(formik.errors.password)}
                            helperText={formik.touched.password && formik.errors.password}

                        />
                        <FormControlLabel
                            control={<Checkbox checked={remember} onChange={(event) => setRemember(event.target.checked)} color="primary" />}
                            label={t('signin.t4')}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{ mt: 3, mb: 2 }}
                        >
                            {t('signin.t3')}
                        </Button>
                        <Grid container>
                            <Grid item xs>
                                <Link href="/forget-password" variant="body2" sx={{ color: 'primary.main', textDecorationColor: 'inherit' }}>
                                {t('signin.t2')}
                                </Link>
                            </Grid>
                        </Grid>
                    </Box>
                    )}
                </Box>
                <Box mt={8}>
                    <Copyright />
                </Box>
            </Container>
        </ThemeProvider >
    );
}
